// LinkedIn Integration Factory
// Factory class for creating and managing LinkedIn integration instances

import { LinkedInIntegration } from './LinkedInIntegration';
import { IntegrationConfig, EncryptedCredentials, IntegrationSettings } from '../../../types/integration';

export interface LinkedInCredentials {
  accessToken: string;
  clientId: string;
  clientSecret: string;
  organizationId?: string;
  personUrn?: string;
}

export interface LinkedInSettings extends IntegrationSettings {
  enableOrganicPosting: boolean;
  enableAnalytics: boolean;
  defaultOrganization?: string;
  postingSchedule?: {
    timezone: string;
    businessHours: {
      start: string; // HH:MM format
      end: string;   // HH:MM format
    };
  };
  contentSettings: {
    maxHashtags: number;
    autoAddHashtags: boolean;
    mentionNotifications: boolean;
  };
}

export interface LinkedInConfig extends IntegrationConfig {
  settings: LinkedInSettings;
}

export class LinkedInIntegrationFactory {
  private static instances: Map<string, LinkedInIntegration> = new Map();

  /**
   * Create a new LinkedIn integration instance
   */
  static async create(config: LinkedInConfig): Promise<LinkedInIntegration> {
    try {
      // Validate configuration
      this.validateConfig(config);

      // Create integration instance
      const integration = new LinkedInIntegration(config);

      // Test connection
      const connectionTest = await integration.testConnection();
      if (!connectionTest.success) {
        throw new Error(`Connection test failed: ${connectionTest.message}`);
      }

      // Connect to LinkedIn
      await integration.connect();

      // Store instance
      this.instances.set(config.id, integration);

      return integration;
    } catch (error) {
      throw new Error(`Failed to create LinkedIn integration: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get existing integration instance
   */
  static getInstance(integrationId: string): LinkedInIntegration | null {
    return this.instances.get(integrationId) || null;
  }

  /**
   * Remove integration instance
   */
  static async removeInstance(integrationId: string): Promise<void> {
    const integration = this.instances.get(integrationId);
    if (integration) {
      await integration.disconnect();
      this.instances.delete(integrationId);
    }
  }

  /**
   * List all active integration instances
   */
  static getActiveInstances(): LinkedInIntegration[] {
    return Array.from(this.instances.values());
  }

  /**
   * Update integration configuration
   */
  static async updateConfig(integrationId: string, newConfig: Partial<LinkedInConfig>): Promise<LinkedInIntegration> {
    const existingIntegration = this.instances.get(integrationId);
    if (!existingIntegration) {
      throw new Error(`Integration ${integrationId} not found`);
    }

    // Disconnect existing integration
    await existingIntegration.disconnect();

    // Create new configuration
    const updatedConfig: LinkedInConfig = {
      ...existingIntegration.getConfig(),
      ...newConfig
    } as LinkedInConfig;

    // Validate new configuration
    this.validateConfig(updatedConfig);

    // Create new integration instance
    const newIntegration = new LinkedInIntegration(updatedConfig);
    
    // Test and connect
    const connectionTest = await newIntegration.testConnection();
    if (!connectionTest.success) {
      throw new Error(`Connection test failed: ${connectionTest.message}`);
    }

    await newIntegration.connect();

    // Replace instance
    this.instances.set(integrationId, newIntegration);

    return newIntegration;
  }

  /**
   * Create integration from setup wizard data
   */
  static async createFromWizard(wizardData: {
    name: string;
    accessToken: string;
    clientId: string;
    clientSecret: string;
    selectedOrganizations: string[];
    enableOrganicPosting: boolean;
    enableAnalytics: boolean;
    timezone: string;
    businessHours: {
      start: string;
      end: string;
    };
  }): Promise<LinkedInIntegration> {
    // Create encrypted credentials (placeholder - in real implementation would encrypt)
    const encryptedCredentials: EncryptedCredentials = {
      encrypted: JSON.stringify({
        accessToken: wizardData.accessToken,
        clientId: wizardData.clientId,
        clientSecret: wizardData.clientSecret,
        organizationId: wizardData.selectedOrganizations[0]
      }),
      keyId: 'default-key',
      algorithm: 'AES-256-GCM',
      createdAt: new Date()
    };

    const config: LinkedInConfig = {
      id: this.generateId(),
      name: wizardData.name,
      type: 'social',
      platform: 'linkedin',
      status: 'active',
      credentials: encryptedCredentials,
      settings: {
        enabled: true,
        autoSync: true,
        batchSize: 50,
        rateLimitPerMinute: 30, // LinkedIn has stricter rate limits
        retryAttempts: 3,
        timeoutMs: 30000,
        customSettings: {},
        enableOrganicPosting: wizardData.enableOrganicPosting,
        enableAnalytics: wizardData.enableAnalytics,
        defaultOrganization: wizardData.selectedOrganizations[0],
        postingSchedule: {
          timezone: wizardData.timezone,
          businessHours: wizardData.businessHours
        },
        contentSettings: {
          maxHashtags: 5, // LinkedIn best practice
          autoAddHashtags: false,
          mentionNotifications: true
        }
      },
      fieldMappings: [],
      syncSchedule: {
        enabled: true,
        frequency: 'real_time',
        timezone: wizardData.timezone,
        batchSize: 50,
        conflictResolution: 'auto_source_wins'
      },
      createdAt: new Date(),
      updatedAt: new Date(),
      healthStatus: {
        status: 'healthy',
        lastCheck: new Date(),
        responseTime: 0,
        errorRate: 0,
        uptime: 100,
        issues: []
      },
      metadata: {
        tags: ['social', 'linkedin', 'professional'],
        category: 'social_media',
        version: '1.0.0',
        supportedFeatures: ['post_publishing', 'analytics', 'organization_management'],
        limitations: ['no_post_scheduling', 'limited_audience_targeting'],
        customData: {}
      }
    };

    return await this.create(config);
  }

  /**
   * Test LinkedIn credentials
   */
  static async testCredentials(credentials: {
    accessToken: string;
    clientId: string;
    clientSecret: string;
  }): Promise<{
    success: boolean;
    message: string;
    userProfile?: {
      id: string;
      name: string;
      profilePicture?: string;
    };
    availableOrganizations?: Array<{
      id: string;
      name: string;
      vanityName: string;
      followerCount: number;
      role: string;
    }>;
  }> {
    try {
      // Create encrypted credentials for testing
      const encryptedCredentials: EncryptedCredentials = {
        encrypted: JSON.stringify(credentials),
        keyId: 'test-key',
        algorithm: 'AES-256-GCM',
        createdAt: new Date()
      };

      // Create temporary integration for testing
      const tempConfig: LinkedInConfig = {
        id: 'temp-test',
        name: 'Test Integration',
        type: 'social',
        platform: 'linkedin',
        status: 'configuring',
        credentials: encryptedCredentials,
        settings: {
          enabled: true,
          autoSync: false,
          batchSize: 50,
          rateLimitPerMinute: 30,
          retryAttempts: 3,
          timeoutMs: 30000,
          customSettings: {},
          enableOrganicPosting: false,
          enableAnalytics: false,
          contentSettings: {
            maxHashtags: 5,
            autoAddHashtags: false,
            mentionNotifications: true
          }
        },
        fieldMappings: [],
        syncSchedule: {
          enabled: false,
          frequency: 'daily',
          timezone: 'UTC',
          batchSize: 50,
          conflictResolution: 'auto_source_wins'
        },
        createdAt: new Date(),
        updatedAt: new Date(),
        healthStatus: {
          status: 'healthy',
          lastCheck: new Date(),
          responseTime: 0,
          errorRate: 0,
          uptime: 100,
          issues: []
        },
        metadata: {
          tags: ['test'],
          category: 'social_media',
          version: '1.0.0',
          supportedFeatures: ['test'],
          limitations: [],
          customData: {}
        }
      };

      const tempIntegration = new LinkedInIntegration(tempConfig);
      
      // Test connection
      const connectionResult = await tempIntegration.testConnection();
      if (!connectionResult.success) {
        return connectionResult;
      }

      // Get user profile and organizations
      await tempIntegration.connect();
      const platforms = await tempIntegration.getConnectedPlatforms();
      
      const availableOrganizations = platforms.map(platform => ({
        id: platform.accountId,
        name: platform.accountName,
        vanityName: platform.accountName.toLowerCase().replace(/\s+/g, ''),
        followerCount: 0, // Would be populated from actual API
        role: 'ADMINISTRATOR' // Would be determined from actual permissions
      }));

      return {
        success: true,
        message: 'Credentials valid',
        userProfile: {
          id: connectionResult.details?.userId || '',
          name: connectionResult.details?.userName || '',
          profilePicture: undefined
        },
        availableOrganizations
      };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Credential test failed'
      };
    }
  }

  /**
   * Get integration health status
   */
  static async getHealthStatus(integrationId: string): Promise<{
    status: 'healthy' | 'warning' | 'error';
    issues: string[];
    lastCheck: Date;
    metrics: {
      postsPublished: number;
      engagementRate: number;
      followerGrowth: number;
      lastSyncTime?: Date;
    };
  }> {
    const integration = this.instances.get(integrationId);
    if (!integration) {
      return {
        status: 'error',
        issues: ['Integration not found'],
        lastCheck: new Date(),
        metrics: {
          postsPublished: 0,
          engagementRate: 0,
          followerGrowth: 0
        }
      };
    }

    try {
      // Test connection
      const connectionTest = await integration.testConnection();
      const issues: string[] = [];
      
      if (!connectionTest.success) {
        issues.push(`Connection failed: ${connectionTest.message}`);
      }

      // Check platform connections
      const platforms = await integration.getConnectedPlatforms();
      const disconnectedPlatforms = platforms.filter(p => !p.connected);
      
      if (disconnectedPlatforms.length > 0) {
        issues.push(`Disconnected platforms: ${disconnectedPlatforms.map(p => p.displayName).join(', ')}`);
      }

      // Check for rate limiting issues
      if (connectionTest.duration && connectionTest.duration > 5000) {
        issues.push('API response times are slow - possible rate limiting');
      }

      // Get metrics (placeholder implementation)
      const metrics = {
        postsPublished: 0, // Would get from analytics
        engagementRate: 0, // Would calculate from recent posts
        followerGrowth: 0, // Would track over time
        lastSyncTime: integration.getLastSyncTime()
      };

      const status = issues.length === 0 ? 'healthy' : 
                    issues.some(issue => issue.includes('Connection failed')) ? 'error' : 'warning';

      return {
        status,
        issues,
        lastCheck: new Date(),
        metrics
      };
    } catch (error) {
      return {
        status: 'error',
        issues: [`Health check failed: ${error instanceof Error ? error.message : 'Unknown error'}`],
        lastCheck: new Date(),
        metrics: {
          postsPublished: 0,
          engagementRate: 0,
          followerGrowth: 0
        }
      };
    }
  }

  /**
   * Validate integration configuration
   */
  private static validateConfig(config: LinkedInConfig): void {
    // Decrypt credentials for validation (in real implementation would properly decrypt)
    let credentials: LinkedInCredentials;
    try {
      credentials = JSON.parse(config.credentials.encrypted) as LinkedInCredentials;
    } catch (error) {
      throw new Error('Invalid encrypted credentials format');
    }

    if (!credentials.accessToken) {
      throw new Error('Access token is required');
    }

    if (!credentials.clientId) {
      throw new Error('Client ID is required');
    }

    if (!credentials.clientSecret) {
      throw new Error('Client secret is required');
    }

    if (!config.settings.enableOrganicPosting && !config.settings.enableAnalytics) {
      throw new Error('At least one feature (organic posting or analytics) must be enabled');
    }

    // Validate business hours format
    if (config.settings.postingSchedule?.businessHours) {
      const { start, end } = config.settings.postingSchedule.businessHours;
      const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
      
      if (!timeRegex.test(start)) {
        throw new Error('Invalid business hours start time format (use HH:MM)');
      }
      
      if (!timeRegex.test(end)) {
        throw new Error('Invalid business hours end time format (use HH:MM)');
      }
    }
  }

  /**
   * Generate unique integration ID
   */
  private static generateId(): string {
    return `linkedin_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}